import React from "react";
import { Link } from "react-router-dom";
import { 
  ArrowRight, TrendingUp, Users, Shield, 
  CheckCircle, Clock, BarChart3, CreditCard,
  Zap, Globe, Award,
  Sparkles, Star, DollarSign,
  Target, ShieldCheck, ZapIcon,
  Cpu, Wallet, ArrowUpRight
} from 'lucide-react';

import heroImage from "../../assets/M1.jpg";

const Home = () => {
  const features = [
    {
      icon: Target,
      title: "Smart Approval",
      description: "AI-powered approval system for instant decisions",
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600"
    },
    {
      icon: ShieldCheck,
      title: "Bank-Grade Security",
      description: "256-bit encryption & secure data protection",
      iconBg: "bg-emerald-100",
      iconColor: "text-emerald-600"
    },
    {
      icon: ZapIcon,
      title: "Lightning Fast",
      description: "Process loans in minutes, not days",
      iconBg: "bg-amber-100",
      iconColor: "text-amber-600"
    },
    {
      icon: Cpu,
      title: "Smart Analytics",
      description: "Real-time insights and predictive analytics",
      iconBg: "bg-purple-100",
      iconColor: "text-purple-600"
    },
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      company: "Johnson & Co.",
      content: "Smart Lend transformed our loan processing. What used to take weeks now takes hours!",
      image: "SJ"
    },
    {
      name: "Michael Chen",
      company: "TechFin Inc.",
      content: "The analytics dashboard is incredible. We've improved our approval accuracy by 40%.",
      image: "MC"
    },
    {
      name: "Priya Sharma",
      company: "Growth Capital",
      content: "Most intuitive lending platform I've used. Our team loves the clean interface.",
      image: "PS"
    },
  ];

  const steps = [
    { number: "01", title: "Apply Online", desc: "Fill our simple application form" },
    { number: "02", title: "Instant Approval", desc: "Get AI-powered approval in minutes" },
    { number: "03", title: "Receive Funds", desc: "Funds deposited directly to your account" },
    { number: "04", title: "Manage & Pay", desc: "Track and repay through dashboard" },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-white">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-to-br from-blue-50 to-purple-50 rounded-full -translate-y-1/2 translate-x-1/2 opacity-70"></div>
        <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-gradient-to-tr from-emerald-50 to-cyan-50 rounded-full translate-y-1/2 -translate-x-1/2 opacity-70"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 rounded-full mb-6 border border-blue-200">
                <Sparkles className="w-4 h-4" />
                <span className="text-sm font-medium">AI-Powered Lending Platform</span>
              </div>
              
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Smart, Secure 
                <span className="block">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Lending</span> Simplified
                </span>
              </h1>
              
              <p className="text-xl text-gray-600 mb-10 max-w-2xl leading-relaxed">
                Experience the future of lending with our intelligent platform that combines cutting-edge technology with exceptional service. Manage loans, track repayments, and grow your business effortlessly.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-16">
                <Link 
                  to="/register" 
                  className="group inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  Start Free Trial
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                
                <Link 
                  to="/login" 
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-700 font-medium rounded-xl border-2 border-gray-300 hover:border-blue-500 hover:text-blue-600 transition-all duration-300"
                >
                  <span className="flex items-center gap-2">
                    <span className="font-semibold">Existing User?</span> Login
                  </span>
                </Link>
              </div>

              {/* Trust Badges */}
              <div className="flex flex-wrap items-center gap-8 pt-8 border-t border-gray-200">
                <div className="text-center">
                  <div className="flex items-center gap-2">
                    <div className="text-3xl font-bold text-gray-900">4.9</div>
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                      ))}
                    </div>
                  </div>
                  <div className="text-sm text-gray-500 mt-1">Customer Rating</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-900">500+</div>
                  <div className="text-sm text-gray-500 mt-1">Happy Clients</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-900">99.9%</div>
                  <div className="text-sm text-gray-500 mt-1">Uptime</div>
                </div>
              </div>
            </div>

            {/* Right Content - Hero Image/Illustration */}
            <div className="relative">
              <div className="relative bg-gradient-to-br from-white to-gray-50 rounded-3xl border border-gray-200 p-6 shadow-xl">
                <div className="absolute -top-4 -right-4 bg-gradient-to-r from-blue-500 to-purple-500 p-4 rounded-2xl shadow-lg">
                  <div className="text-white text-sm font-semibold">Live Dashboard</div>
                </div>
                
                {/* Mini Stats */}
                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-5 rounded-2xl border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-2xl font-bold text-gray-900">$2.5M+</div>
                        <div className="text-sm text-gray-600">Total Loans</div>
                      </div>
                      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                        <Wallet className="w-6 h-6 text-blue-600" />
                      </div>
                    </div>
                    <div className="mt-3 flex items-center gap-1 text-green-600 text-sm">
                      <ArrowUpRight className="w-4 h-4" />
                      <span>+12% this month</span>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-5 rounded-2xl border border-purple-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-2xl font-bold text-gray-900">94%</div>
                        <div className="text-sm text-gray-600">Repayment Rate</div>
                      </div>
                      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                        <TrendingUp className="w-6 h-6 text-purple-600" />
                      </div>
                    </div>
                    <div className="mt-3 flex items-center gap-1 text-green-600 text-sm">
                      <ArrowUpRight className="w-4 h-4" />
                      <span>+5% from last quarter</span>
                    </div>
                  </div>
                </div>
                
                {/* Mini Chart */}
                <div className="relative h-48 bg-gradient-to-b from-white to-gray-50 rounded-2xl border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="font-semibold text-gray-900">Active Loans</div>
                    <div className="text-sm text-gray-500">Last 6 months</div>
                  </div>
                  <div className="relative h-32">
                    {/* Simple chart visualization */}
                    <div className="absolute inset-0 flex items-end justify-between px-4">
                      {[40, 60, 80, 70, 90, 100].map((height, i) => (
                        <div key={i} className="flex flex-col items-center">
                          <div 
                            className="w-8 bg-gradient-to-t from-blue-500 to-blue-400 rounded-t-lg transition-all duration-300 hover:h-24"
                            style={{ height: `${height}%` }}
                          ></div>
                          <div className="text-xs text-gray-500 mt-2">{['J', 'F', 'M', 'A', 'M', 'J'][i]}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating Elements */}
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-2xl shadow-xl border border-gray-200">
                <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-green-500 rounded-xl flex items-center justify-center">
                  <Shield className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 rounded-full mb-4">
              <span className="font-medium">Simple Process</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Get from application to approval in just a few simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  <div className="text-5xl font-bold text-gray-100 mb-4">{step.number}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600">{step.desc}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 right-0 w-8 h-0.5 bg-gradient-to-r from-blue-200 to-purple-200 -translate-y-1/2 translate-x-4"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 rounded-full mb-4">
              <Zap className="w-4 h-4" />
              <span className="font-medium">Key Features</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Smart Lend</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Powerful features designed to streamline your lending operations
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={index}
                  className="group bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-xl hover:border-blue-300 transition-all duration-300"
                >
                  <div className={`inline-flex p-4 rounded-xl ${feature.iconBg} mb-6`}>
                    <Icon className={`w-6 h-6 ${feature.iconColor}`} />
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 mb-6">{feature.description}</p>
                  
                  <div className="pt-4 border-t border-gray-100">
                    <Link to="/login" className="inline-flex items-center gap-2 text-blue-600 font-medium cursor-pointer">
                      Learn more
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 rounded-full mb-4">
              <Award className="w-4 h-4" />
              <span className="font-medium">Client Testimonials</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Trusted by Industry Leaders</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              See what our clients have to say about their experience
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl border border-gray-200 p-8 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center gap-2 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                  ))}
                </div>
                
                <p className="text-gray-700 text-lg mb-8 leading-relaxed">"{testimonial.content}"</p>
                
                <div className="flex items-center gap-4 pt-6 border-t border-gray-100">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">
                    {testimonial.image}
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative overflow-hidden py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25px 25px, white 2%, transparent 0%)`,
            backgroundSize: '50px 50px'
          }}></div>
        </div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Lending?
          </h2>
          
          <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
            Join thousands of businesses using Smart Lend to streamline their lending operations. Start your free trial today.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link 
              to="/register" 
              className="group inline-flex items-center justify-center px-8 py-4 bg-white text-blue-700 font-semibold rounded-xl hover:bg-blue-50 transition-all duration-300 shadow-2xl hover:shadow-3xl transform hover:-translate-y-1"
            >
              Start Free Trial
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            
            <Link 
              to="/contact" 
              className="inline-flex items-center justify-center px-8 py-4 bg-transparent text-white font-medium rounded-xl border-2 border-white/30 hover:bg-white/10 transition-all duration-300"
            >
              Request Demo
            </Link>
          </div>

          {/* Features List */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto opacity-90">
            <div className="text-center">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <div className="text-sm text-white">No credit card required</div>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div className="text-sm text-white">30-day free trial</div>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div className="text-sm text-white">Dedicated support</div>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div className="text-sm text-white">Enterprise security</div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Section */}
      <section className="py-12 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-6 md:mb-0">
              <div className="text-2xl font-bold text-gray-900">Smart Lend</div>
              <p className="text-gray-600 text-sm mt-2">Intelligent Lending Platform</p>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/about" className="text-gray-600 hover:text-blue-600 transition-colors">About</Link>
              <Link to="/contact" className="text-gray-600 hover:text-blue-600 transition-colors">Contact</Link>
              <Link to="/FAQ" className="text-gray-600 hover:text-blue-600 transition-colors">FAQ</Link>
              {/* <a href="#" className="text-gray-600 hover:text-blue-600 transition-colors">Contact</a> */}
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-200 text-center text-gray-500 text-sm">
            © {new Date().getFullYear()} Smart Lend. All rights reserved.
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;